import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import AnimatedModal from "@/components/base/AnimatedModal";

interface BizProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  company_name: string;
  role: string;
  roles: string[];
  status: string;
  created_at: string;
  updated_at: string;
}

export default function BusinessManagement() {
  const [businesses, setBusinesses] = useState<BizProfile[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [detailTarget, setDetailTarget] = useState<BizProfile | null>(null);

  const fetchBusinesses = useCallback(async () => {
    try {
      setError(null);
      const { data, error: supabaseError } = await supabase
        .from("vietnamcv_profiles")
        .select("id, name, email, phone, company_name, role, roles, status, created_at, updated_at")
        .contains("roles", ["business"])
        .order("created_at", { ascending: false });

      if (supabaseError) throw supabaseError;
      setBusinesses((data || []) as BizProfile[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const handleToggleStatus = async (biz: BizProfile) => {
    const newStatus = biz.status === "banned" ? "active" : "banned";
    setTogglingId(biz.id);

    const { error: updateError } = await supabase
      .from("vietnamcv_profiles")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", biz.id);

    if (updateError) {
      alert("Lỗi khi cập nhật trạng thái: " + updateError.message);
      setTogglingId(null);
      return;
    }

    setBusinesses((prev) =>
      prev.map((b) => (b.id === biz.id ? { ...b, status: newStatus } : b))
    );
    setTogglingId(null);
  };

  const filtered = businesses.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return b.name.toLowerCase().includes(q) || b.email.toLowerCase().includes(q) || (b.company_name && b.company_name.toLowerCase().includes(q));
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("vi-VN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      {/* Business Detail Modal */}
      <AnimatedModal isOpen={!!detailTarget} onClose={() => setDetailTarget(null)} variant="fade-up">
        <div className="bg-background-50 rounded-2xl w-[92vw] max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
          <div className="sticky top-0 bg-background-50 flex items-center justify-between px-6 py-4 border-b border-background-200 z-10">
            <h2 className="font-heading font-bold text-lg text-foreground-950">Thông tin doanh nghiệp</h2>
            <button
              onClick={() => setDetailTarget(null)}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer text-foreground-500"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {detailTarget && (
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-accent-100 rounded-xl flex-shrink-0">
                  <i className="ri-building-line text-accent-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-foreground-950">{detailTarget.company_name}</h3>
                  {detailTarget.status === "banned" && (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200 mt-1">
                      Đã chặn
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-background-100 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                    <i className="ri-user-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Người đại diện</p>
                    <p className="text-sm font-medium text-foreground-900">{detailTarget.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-background-100 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Email</p>
                    <p className="text-sm font-medium text-foreground-900">{detailTarget.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-background-100 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Số điện thoại</p>
                    <p className="text-sm font-medium text-foreground-900">{detailTarget.phone || "Chưa cập nhật"}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-background-100 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className="ri-calendar-line text-amber-600"></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Ngày đăng ký</p>
                    <p className="text-sm font-medium text-foreground-900">{formatDate(detailTarget.created_at)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-background-100 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: detailTarget.status === "banned" ? "#fef2f2" : "#f0fdf4" }}>
                    <i className={`${detailTarget.status === "banned" ? "ri-shield-cross-line text-red-600" : "ri-shield-check-line text-green-600"}`}></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Trạng thái</p>
                    <p className={`text-sm font-semibold ${detailTarget.status === "banned" ? "text-red-600" : "text-green-600"}`}>
                      {detailTarget.status === "banned" ? "Đã chặn" : "Đang hoạt động"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-background-200">
                <button
                  onClick={() => setDetailTarget(null)}
                  className="w-full px-4 py-2.5 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Đóng
                </button>
              </div>
            </div>
          )}
        </div>
      </AnimatedModal>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">Danh sách doanh nghiệp</h3>
          <p className="text-xs text-foreground-500 mt-0.5">{businesses.length} doanh nghiệp đã đăng ký</p>
        </div>

        <div className="relative w-full sm:w-56">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm doanh nghiệp..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div className="p-10 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-9 h-9 rounded-lg bg-background-200"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-background-200/80 rounded"></div>
                  <div className="h-3 w-1/4 bg-background-200/50 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-xl text-red-500"></i>
            </div>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              onClick={() => { setLoading(true); fetchBusinesses(); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-sm"></i>
              <span>Thử lại</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
          <div>
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Doanh nghiệp</th>
                  <th className="text-right pr-5 py-3.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap w-auto">Chức năng</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((biz) => (
                  <tr key={biz.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    <td className="px-5 py-4">
                      <button
                        onClick={() => setDetailTarget(biz)}
                        className="flex items-center gap-3 group cursor-pointer text-left"
                      >
                        <div className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                          <i className="ri-building-line text-accent-600 text-base"></i>
                        </div>
                        <div>
                          <span className="font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors flex items-center gap-1.5">
                            {biz.company_name}
                            <i className="ri-external-link-line text-xs text-foreground-400 group-hover:text-primary-500"></i>
                          </span>
                          {biz.status === "banned" && (
                            <span className="ml-0 mt-0.5 block sm:inline sm:ml-2 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200 w-fit">
                              Đã chặn
                            </span>
                          )}
                        </div>
                      </button>
                    </td>
                    <td className="pl-0 pr-5 py-4 text-right">
                      <div className="relative inline-block">
                        <select
                          value={biz.status}
                          onChange={() => handleToggleStatus(biz)}
                          disabled={togglingId === biz.id}
                          className={`appearance-none pl-2.5 pr-7 py-1.5 text-xs font-semibold rounded-full border cursor-pointer transition-all ${
                            biz.status === "banned"
                              ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                              : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                          } disabled:opacity-50 disabled:cursor-wait`}
                        >
                          <option value="active">Hoạt động</option>
                          <option value="banned">Chặn</option>
                        </select>
                        {togglingId === biz.id && (
                          <i className="ri-loader-4-line animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-xs"></i>
                        )}
                        <i className={`ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-xs pointer-events-none ${
                          biz.status === "banned" ? "text-red-500" : "text-green-500"
                        } ${togglingId === biz.id ? "opacity-0" : ""}`}></i>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 flex items-center justify-center bg-background-100 rounded-full">
                          <i className="ri-building-2-line text-2xl text-foreground-400"></i>
                        </div>
                        <p className="text-sm text-foreground-500">Không tìm thấy doanh nghiệp nào</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 px-2">
        <p className="text-xs text-foreground-500">
          Hiển thị <strong>{filtered.length}</strong>/{businesses.length} doanh nghiệp
        </p>
      </div>
    </div>
  );
}